/**
 * Bed PDP — order summary box (snippets/product-bundle-summary.liquid).
 * Bed row follows the selected variant, mattress row follows `mattress:change`
 * from <mattress-upsell>; total = (bed + mattress) × quantity. The sticky ATC
 * price shows the same total while a mattress is picked.
 */
if (!customElements.get('bundle-summary')) {
  class BundleSummary extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      try {
        this.bedVariants = JSON.parse(this.querySelector('[data-bed-variants]').textContent);
      } catch (e) {
        this.bedVariants = {};
      }
      this.mattress = document.querySelector('mattress-upsell')?.selection || null;

      this.onMattressChange = (event) => {
        this.mattress = event.detail?.selection || null;
        this.render();
      };
      // Defer so the sticky bar's own variant update runs first, then gets our total.
      this.onVariantChange = () => setTimeout(() => this.render(), 0);
      this.onQuantityChange = (event) => {
        if (event.target.matches?.('input[name="quantity"]')) this.render();
      };

      document.addEventListener('mattress:change', this.onMattressChange);
      document.addEventListener('variant:changed', this.onVariantChange);
      document.addEventListener('change', this.onQuantityChange);
      document.addEventListener('input', this.onQuantityChange);
      document.addEventListener('click', this.onQuantityChange);

      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('mattress:change', this.onMattressChange);
      document.removeEventListener('variant:changed', this.onVariantChange);
      document.removeEventListener('change', this.onQuantityChange);
      document.removeEventListener('input', this.onQuantityChange);
      document.removeEventListener('click', this.onQuantityChange);
    }

    get form() {
      return document.getElementById(this.dataset.formId);
    }

    formatMoney(cents) {
      const format = window.FoxTheme?.settings?.moneyFormat;
      if (window.FoxTheme?.Currency?.formatMoney && format) {
        return FoxTheme.Currency.formatMoney(cents, format);
      }
      return `${Math.round(cents / 100).toLocaleString('en-US')} ₪`;
    }

    get quantity() {
      const input = document.querySelector(`input[name="quantity"][form="${this.dataset.formId}"]`) ||
        this.form?.querySelector('input[name="quantity"]');
      return Math.max(parseInt(input?.value, 10) || 1, 1);
    }

    render() {
      const variantId = this.form?.querySelector('input[name="id"]')?.value;
      const bed = this.bedVariants[String(variantId)];
      if (!bed) return;

      const qty = this.quantity;
      const mattressPrice = this.mattress ? this.mattress.price : 0;
      const unitTotal = bed.price + mattressPrice;

      const bedVariantEl = this.querySelector('[data-summary-bed-variant]');
      if (bedVariantEl) bedVariantEl.textContent = bed.title;
      this.querySelector('[data-summary-bed-price]').textContent = this.formatMoney(bed.price);

      const mattressEl = this.querySelector('[data-summary-mattress]');
      const mattressPriceEl = this.querySelector('[data-summary-mattress-price]');
      if (this.mattress) {
        mattressEl.textContent = [this.mattress.title, this.mattress.size].filter(Boolean).join(' · ');
        mattressEl.classList.remove('is-empty');
        mattressPriceEl.textContent = this.formatMoney(this.mattress.price);
      } else {
        mattressEl.textContent = this.dataset.emptyText || '';
        mattressEl.classList.add('is-empty');
        mattressPriceEl.textContent = '';
      }

      this.querySelector('[data-summary-qty]').textContent = qty > 1 ? `× ${qty}` : '';
      this.querySelector('[data-summary-total]').textContent = this.formatMoney(unitTotal * qty);

      // Sticky ATC bar: per-unit bed + mattress price, compare-at summed the same way.
      const compareTotal =
        Math.max(bed.compare || 0, bed.price) + (this.mattress ? Math.max(this.mattress.compare || 0, this.mattress.price) : 0);
      document.querySelectorAll('.sticky-atc-bar [data-sticky-price]').forEach((el) => {
        el.textContent = this.formatMoney(unitTotal);
      });
      document.querySelectorAll('.sticky-atc-bar [data-sticky-compare]').forEach((wrap) => {
        const valueEl = wrap.querySelector('[data-sticky-compare-value]');
        if (!valueEl) return;
        const show = compareTotal > unitTotal;
        valueEl.textContent = show ? this.formatMoney(compareTotal) : '';
        wrap.classList.toggle('hidden', !show);
        wrap.toggleAttribute('aria-hidden', !show);
      });
    }
  }

  customElements.define('bundle-summary', BundleSummary);
}
